import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { PREFECTURES } from '../utils/constants';
import { compressImageFile } from '../utils/imageHelpers';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import './StorySubmission.css';

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

function StorySubmission() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    authorName: '',
    contactEmail: currentUser?.email || '',
    title: '',
    mountainName: '',
    prefecture: '',
    climbedAt: '',
    story: '',
    imageCaption: '',
    socialHandle: '',
    consentToPublish: false,
    consentFromAllPeople: false,
  });

  useEffect(() => {
    let isMounted = true;

    async function hydrateProfile() {
      if (!currentUser) {
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists() || !isMounted) {
          return;
        }

        const profile = userDoc.data();
        setFormData((prev) => ({
          ...prev,
          authorName: profile.nickname || profile.displayName || currentUser.displayName || prev.authorName,
          contactEmail: currentUser.email || prev.contactEmail,
        }));
      } catch (profileError) {
        console.error('Failed to load profile for story submission:', profileError);
      }
    }

    hydrateProfile();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const remainingChars = useMemo(() => 600 - formData.story.length, [formData.story.length]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください。');
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setError('画像は 8MB 以下でアップロードしてください。');
      return;
    }

    try {
      setError('');
      const compressed = await compressImageFile(file, {
        maxWidth: 1600,
        maxHeight: 1200,
        quality: 0.86,
        outputType: 'image/jpeg',
      });

      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    } catch (imageError) {
      console.error('Story image compression failed:', imageError);
      setError('画像の圧縮に失敗しました。別の画像で再度お試しください。');
    }
  };

  const validateForm = () => {
    if (!formData.authorName.trim()) return '表示名を入力してください。';
    if (!formData.contactEmail.trim()) return '採用時のご連絡先メールアドレスを入力してください。';
    if (!formData.title.trim()) return 'タイトルを入力してください。';
    if (!formData.mountainName.trim()) return '山名を入力してください。';
    if (!formData.prefecture) return 'エリアを選択してください。';
    if (!formData.story.trim()) return '体験談を入力してください。';
    if (formData.story.trim().length < 80) return '体験談は 80 文字以上で入力してください。';
    if (!imageFile) return '掲載候補の写真を 1 枚アップロードしてください。';
    if (!formData.consentToPublish) return '掲載候補として利用される可能性への同意が必要です。';
    if (!formData.consentFromAllPeople) return '写真に映った方全員のご了承確認が必要です。';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!currentUser) {
      navigate('/login');
      return;
    }

    setSubmitting(true);

    try {
      const moderation = await guardContentSubmission({
        action: 'story_submission',
        contentType: 'story_submission',
        texts: [
          formData.authorName,
          formData.title,
          formData.mountainName,
          formData.story,
          formData.imageCaption,
          formData.socialHandle,
        ],
        metadata: {
          prefecture: formData.prefecture,
        },
      });
      showModerationWarning(moderation);

      const imagePath = `story_submission_images/${currentUser.uid}_${Date.now()}.jpg`;
      const storageRef = ref(storage, imagePath);

      await uploadBytes(storageRef, imageFile, {
        contentType: imageFile.type || 'image/jpeg',
      });

      const imageUrl = await getDownloadURL(storageRef);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const profile = userDoc.exists() ? userDoc.data() : {};

      await addDoc(collection(db, 'storySubmissions'), {
        userId: currentUser.uid,
        authorName: formData.authorName.trim(),
        contactEmail: formData.contactEmail.trim(),
        title: formData.title.trim(),
        mountainName: formData.mountainName.trim(),
        prefecture: formData.prefecture,
        climbedAt: formData.climbedAt || null,
        story: formData.story.trim(),
        imageCaption: formData.imageCaption.trim(),
        socialHandle: formData.socialHandle.trim(),
        imageUrl,
        imagePath,
        imageName: imageFile.name,
        status: 'pending',
        consentToPublish: true,
        consentFromAllPeople: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        authorSnapshot: {
          uid: currentUser.uid,
          nickname: profile.nickname || '',
          displayName: currentUser.displayName || profile.displayName || '',
          photoURL: profile.photoURL || currentUser.photoURL || '',
        },
      });

      setSuccess(true);
    } catch (submitError) {
      console.error('Story submission failed:', submitError);
      setError('投稿の保存に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="story-submission-page">
        <nav className="story-submission-breadcrumb">
          <Link to="/">ホーム</Link> / <span>体験談投稿</span>
        </nav>

        <section className="story-submission-shell story-submission-shell--compact">
          <p className="story-submission-eyebrow">Thanks</p>
          <h1>体験談を受け付けました</h1>
          <p className="story-submission-lead">
            内容を確認のうえ、掲載候補として社内確認を進めます。掲載採用となった場合は、
            入力いただいたメールアドレス宛に 3,000 円分の Amazon ギフト券送付案内をご連絡します。
          </p>
          <div className="story-submission-actions">
            <button type="button" className="story-submit-btn" onClick={() => navigate('/')}>
              トップへ戻る
            </button>
            <button
              type="button"
              className="story-submit-btn story-submit-btn--ghost"
              onClick={() => {
                setSuccess(false);
                setImageFile(null);
                setImagePreview('');
                setFormData((prev) => ({
                  ...prev,
                  title: '',
                  mountainName: '',
                  prefecture: '',
                  climbedAt: '',
                  story: '',
                  imageCaption: '',
                  socialHandle: '',
                  consentToPublish: false,
                  consentFromAllPeople: false,
                }));
              }}
            >
              続けて投稿する
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="story-submission-page">
      {submitting && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          送信中...
        </div>
      )}
      <nav className="story-submission-breadcrumb">
        <Link to="/">ホーム</Link> / <span>体験談投稿</span>
      </nav>

      <section className="story-submission-shell">
        <header className="story-submission-hero">
          <p className="story-submission-eyebrow">Story Program</p>
          <h1>掲載候補の体験談を募集しています</h1>
          <p className="story-submission-lead">
            ヤマッチで生まれた出会いや山行の体験談を募集します。採用された投稿はトップページや紹介コンテンツに掲載し、
            採用者には 3,000 円分の Amazon ギフト券をお送りします。
          </p>
          <p className="story-submission-lead story-submission-lead--sub">
            サイト掲載に採用された場合、3,000 円分の Amazon ギフト券をお送りします。
            1〜3 名の投稿画像を採用予定です。この募集は予告なく終了する場合がございます。
            採用者以外にはご連絡がありませんので、ご了承ください。
          </p>
        </header>

        <div className="story-submission-grid">
          <aside className="story-guidance-card">
            <h2>掲載前提のご案内</h2>
            <ul className="story-guidance-list">
              <li>投稿画像および体験談は、サイトに掲載される場合があります。</li>
              <li>サイト掲載に採用された場合、3,000 円分の Amazon ギフト券をお送りします。</li>
              <li>1〜3 名の投稿画像を採用予定です。この募集は予告なく終了する場合があります。</li>
              <li>採用者以外にはご連絡がありませんので、ご了承ください。</li>
              <li>写真に映った方全員のご了承の上、投稿をお願いします。</li>
              <li>ご連絡は入力いただいたメールアドレス宛に行います。</li>
            </ul>

            <h3>掲載で求める写真</h3>
            <ul className="story-guidance-list">
              <li>登山の雰囲気や一緒に過ごした様子が伝わる、明るく見やすい写真</li>
              <li>ピンぼけや暗すぎる写真、スクリーンショット、文字だらけの画像は避ける</li>
              <li>第三者の権利を侵害するロゴ・転載画像・無断撮影画像は使わない</li>
              <li>推奨は横長または正方形、1 枚で内容が伝わる写真です</li>
            </ul>
          </aside>

          <form className="story-form-card" onSubmit={handleSubmit}>
            <div className="story-form-row story-form-row--two">
              <label>
                <span>表示名</span>
                <input type="text" name="authorName" value={formData.authorName} onChange={handleChange} maxLength={40} placeholder="ニックネーム推奨" />
              </label>
              <label>
                <span>連絡先メールアドレス</span>
                <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} placeholder="gift@example.com" />
              </label>
            </div>

            <div className="story-form-row story-form-row--two">
              <label>
                <span>タイトル</span>
                <input type="text" name="title" value={formData.title} onChange={handleChange} maxLength={60} placeholder="例: 初めての北アルプスで友だちができた日" />
              </label>
              <label>
                <span>山名</span>
                <input type="text" name="mountainName" value={formData.mountainName} onChange={handleChange} maxLength={40} placeholder="例: 燕岳" />
              </label>
            </div>

            <div className="story-form-row story-form-row--two">
              <label>
                <span>エリア</span>
                <select name="prefecture" value={formData.prefecture} onChange={handleChange}>
                  <option value="">選択してください</option>
                  {PREFECTURES.map((prefecture) => (
                    <option key={prefecture} value={prefecture}>
                      {prefecture}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>山行日</span>
                <input type="date" name="climbedAt" value={formData.climbedAt} onChange={handleChange} />
              </label>
            </div>

            <label>
              <span>体験談</span>
              <textarea
                name="story"
                value={formData.story}
                onChange={handleChange}
                rows={8}
                maxLength={600}
                placeholder="どんな募集で出会い、当日どんな体験があり、なぜ印象に残ったのかを書いてください。"
              />
              <small className={remainingChars < 80 ? 'story-char-count story-char-count--warn' : 'story-char-count'}>
                残り {remainingChars} 文字
              </small>
            </label>

            <div className="story-form-row story-form-row--two">
              <label>
                <span>写真の補足</span>
                <input type="text" name="imageCaption" value={formData.imageCaption} onChange={handleChange} maxLength={80} placeholder="例: 稜線で休憩している場面" />
              </label>
              <label>
                <span>SNSアカウント名</span>
                <input type="text" name="socialHandle" value={formData.socialHandle} onChange={handleChange} maxLength={40} placeholder="掲載時に紹介したい場合のみ" />
              </label>
            </div>

            <label className="story-upload-field">
              <span>掲載候補の写真 1 枚</span>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              <small>JPG / PNG / WebP。アップロード前に圧縮します。</small>
            </label>

            {imagePreview && (
              <div className="story-image-preview">
                <img src={imagePreview} alt="投稿予定のプレビュー" />
              </div>
            )}

            <div className="story-checkboxes">
              <label className="story-check">
                <input type="checkbox" name="consentToPublish" checked={formData.consentToPublish} onChange={handleChange} />
                <span>投稿画像および体験談がサイトに掲載される場合があることに同意します。</span>
              </label>
              <label className="story-check">
                <input type="checkbox" name="consentFromAllPeople" checked={formData.consentFromAllPeople} onChange={handleChange} />
                <span>写真に映った方全員のご了承の上、投稿しています。</span>
              </label>
            </div>

            {error && <p className="story-form-error">{error}</p>}

            <div className="story-submit-actions">
              <button type="submit" className="story-submit-btn" disabled={submitting}>
                {submitting ? '投稿を保存中...' : '体験談を投稿する'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default StorySubmission;
